import * as React from "react";
import type { PaginationProps } from "../Pagination";

export enum SortOrder {
  ascend = "ascend",
  descend = "descend",
}

export type TableSorter = {
  field: React.Key;
  order: SortOrder;
};

export type TableColumn<T> = {
  key?: React.Key;
  title?: React.ReactNode;
  className?: string;
  classNames?: {
    cell?: string;
    header?: string;
  };
  sorter?: boolean;
  dataIndex?: keyof T & string;
  render?: (
    value: string | number | boolean | null | undefined | unknown[],
    record: T,
    meta: {
      index: number;
      expanded: boolean;
      toggleExpand?: () => void;
    },
  ) => React.ReactNode | string;
};

export type ExpandableConfig<T> = {
  expandedRowRender: (record: T, index: number) => React.ReactNode;
  rowExpandable?: (record: T) => boolean;
  expandedRowKeys?: React.Key[];
  defaultExpandedRowKeys?: React.Key[];
  onExpand?: (expanded: boolean, record: T) => void;
  /**
   * When true, only one row can be expanded at a time (accordion behavior).
   */
  accordion?: boolean;
  /**
   * Called whenever expanded keys should change. Useful for controlled mode.
   */
  onExpandedRowKeysChange?: (keys: React.Key[]) => void;
};

export type TableProps<T> = {
  columns: Array<TableColumn<T>>;
  onColumnClick?: (column: TableColumn<T>) => void;
  dataSource: T[];
  rowKey?: (keyof T & string) | ((record: T, index?: number) => React.Key);
  className?: string;
  loading?: boolean;
  pagination?: PaginationProps;
  scroll?: { x?: number | true | string; y?: number | string };
  expandable?: ExpandableConfig<T>;
  onRowClick?: (record: T, index: number) => void;
  locale?: { emptyText?: React.ReactNode };
  sorter?: TableSorter;
  onChange?: (payload: { sorter?: TableSorter }) => void;
};

export type InternalSorterState = {
  columnKey: React.Key;
  order: SortOrder;
} | null;
